import { useMutation, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  identityEntityCreateBusinessEntityMutation,
  identityEntityCreatePersonEntityMutation,
  identityEntityGetEntityOptions,
  identityEntityUpdateAssociatedPersonsMutation,
  identityEntityUpdateBusinessEntityMutation,
  identityEntityUpdatePersonEntityMutation,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button, Input, Separator } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading, RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

import { BusinessEntity } from './_partials/BusinessEntity';
import { EntityTypeSelector } from './_partials/EntityTypeSelector';
import { PersonEntity } from './_partials/PersonEntity';
import { useAllAssociatedPersons } from './_util/useAllAssociatedPersons';
import {
  BusinessEntityFormValues,
  EntityFormType,
  PersonEntityFormValues,
  associatedPersonRecordToFormValues,
  buildBusinessCreateRequest,
  buildBusinessUpdateRequest,
  buildPersonCreateRequest,
  buildPersonUpdateRequest,
  buildUpdateAssociatedPersonsRequest,
  createDefaultPersonFormValues,
  createInitialBusinessFormState,
  getBusinessFormValuesFromEntity,
  getPersonFormValuesFromEntity,
  useEntityForm,
} from './_util/useEntityForm';

export { EntityFormType as EntityTypes };

interface Params {
  id: string;
  type: string;
}

export const PageEntitiesEdit: React.FC = () => {
  const { currentUser, isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { handleSubmitErrors } = useFormErrorHandler();
  const { id, type: typeParam } = useParams<keyof Params>() as Params;
  const type = (
    typeParam === EntityFormType.Person || typeParam === EntityFormType.Business ? typeParam : EntityFormType.Person
  ) as EntityFormType;
  // The form on screen. When creating, picking the other type fades this form out before the swap.
  const [displayType, setDisplayType] = useState<EntityFormType>(type);

  const form = useEntityForm(displayType);

  const createPersonMutation = useMutation(identityEntityCreatePersonEntityMutation());
  const updatePersonMutation = useMutation(identityEntityUpdatePersonEntityMutation());
  const createBusinessMutation = useMutation(identityEntityCreateBusinessEntityMutation());
  const updateBusinessMutation = useMutation(identityEntityUpdateBusinessEntityMutation());
  const updateAssociatedPersonsMutation = useMutation(identityEntityUpdateAssociatedPersonsMutation());

  // Once the business is created we keep its id so a retry after a partial failure updates
  // (not re-creates) the business and re-sends the associated-persons set — nothing entered
  // is lost, and the reconcile PUT is idempotent so retrying it is safe.
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(null);

  const entityQueryOptions = identityEntityGetEntityOptions({ path: { entity_id: id ?? '' } });
  const { data: entityData, isLoading } = useQuery({
    ...entityQueryOptions,
    enabled: !!id && !!currentUser,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (String(apiError?.code) === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // On a business edit we load the linked associated persons separately: the entity response's
  // beneficial_owners carry no person_entity_id, so this list is the only source of the real ids
  // the form needs to hydrate each link and pass validation. Every page is loaded because the save
  // reconciles with a full-replacement PUT.
  const isBusinessEdit = Boolean(id) && type === EntityFormType.Business;
  const associatedPersons = useAllAssociatedPersons(id, { enabled: isBusinessEdit && !!currentUser });

  useEffect(() => {
    if (!entityData) {
      return;
    }

    if (type === EntityFormType.Person) {
      form.reset(getPersonFormValuesFromEntity(entityData));
      return;
    }

    // Wait only while the list is still loading. If it errors, fall through so the business fields
    // still initialize — associated persons are seeded only from a fully loaded set, and the save
    // skips the reconcile unless that set is complete, so a failed/partial load can't wipe links.
    if (isBusinessEdit && associatedPersons.isLoading) {
      return;
    }

    const businessValues = getBusinessFormValuesFromEntity(entityData);
    if (isBusinessEdit && associatedPersons.isComplete) {
      businessValues.associatedPersons = associatedPersons.associatedPersons.map(associatedPersonRecordToFormValues);
    }
    form.reset(businessValues);
  }, [
    entityData,
    type,
    form,
    isBusinessEdit,
    associatedPersons.isLoading,
    associatedPersons.isComplete,
    associatedPersons.associatedPersons,
  ]);

  // Surface a load failure once: the associated-persons section will render empty, and the save
  // deliberately skips the reconcile while the set is incomplete, so the user needs to know their
  // edits to that section won't persist until the list loads (e.g. on a reload).
  const associatedPersonsErrorNotified = useRef(false);
  useEffect(() => {
    if (isBusinessEdit && associatedPersons.isError && !associatedPersonsErrorNotified.current) {
      associatedPersonsErrorNotified.current = true;
      addDangerNotification({
        content:
          'Could not load the associated persons for this business. You can still edit its details; associated persons were left unchanged.',
      });
    }
  }, [isBusinessEdit, associatedPersons.isError, addDangerNotification]);

  const isFormShown = Boolean(id) || displayType === type;

  const handleFormExited = () => {
    if (!id && displayType !== type) {
      // Reset before the swap so the new form's first render already has its own defaults.
      form.reset(type === EntityFormType.Person ? createDefaultPersonFormValues() : createInitialBusinessFormState());
      setDisplayType(type);
    }
  };

  useEffect(() => {
    if (!id) {
      if (displayType === EntityFormType.Person) {
        form.reset(createDefaultPersonFormValues());
      } else {
        form.reset(createInitialBusinessFormState());
      }
    }
  }, [id, displayType, form]);

  // Reconciles the business's associated persons to exactly the set on the form via a single PUT:
  // the endpoint upserts each entry (so edits to an existing person's roles/ownership/title persist)
  // and removes any link no longer present. Idempotent, so it is safe to re-run on a retry. Throws
  // on failure so the caller's try/catch surfaces the error and skips the success path.
  const saveAssociatedPersons = async (entityId: string, businessValues: BusinessEntityFormValues): Promise<void> => {
    const body = buildUpdateAssociatedPersonsRequest(businessValues);
    // The reconcile endpoint rejects an empty set, so a business with no associated persons would
    // fail an otherwise-valid save. With nothing to add or remove there is nothing to reconcile —
    // skip the PUT.
    if (body.associated_persons.length === 0) {
      return;
    }
    await updateAssociatedPersonsMutation.mutateAsync({ path: { entity_id: entityId }, body });
  };

  const handleSubmit = form.handleSubmit(async () => {
    if (!currentUser) return;

    const values = form.getValues() as PersonEntityFormValues | BusinessEntityFormValues;

    // Which identification fields the user actually changed. Updates are PATCH-style, so on an edit
    // only the changed one is submitted — this lets a business that already stores both an EIN and a
    // registration ID (which the API keeps, but won't accept together in one request) still be saved.
    const dirtyFields = form.formState.dirtyFields as {
      ein?: boolean;
      registrationId?: boolean | Record<string, boolean>;
    };
    const registrationIdDirty =
      dirtyFields.registrationId === true ||
      (typeof dirtyFields.registrationId === 'object' && Object.values(dirtyFields.registrationId).some(Boolean));
    const changedIdentification = {
      ein: Boolean(dirtyFields.ein),
      registrationId: Boolean(registrationIdDirty),
    };

    try {
      if (id) {
        if (type === EntityFormType.Person) {
          const payload = buildPersonUpdateRequest(values as PersonEntityFormValues);
          const response = await updatePersonMutation.mutateAsync({ path: { entity_id: id }, body: payload });
          addSuccessNotification({
            content: `${response.person_details?.first_name} ${response.person_details?.last_name} updated`,
          });
        } else {
          const businessValues = values as BusinessEntityFormValues;
          const response = await updateBusinessMutation.mutateAsync({
            path: { entity_id: id },
            body: buildBusinessUpdateRequest(businessValues, changedIdentification),
          });

          // Reconcile associated persons (add/update/remove) to match the form. Guarded on the
          // fully loaded baseline: if the list failed or is incomplete, the form's associated
          // persons aren't trustworthy, so skip the PUT rather than risk wiping existing links.
          if (associatedPersons.isComplete) {
            await saveAssociatedPersons(id, businessValues);
          }

          addSuccessNotification({
            content: `${response.business_details?.business_name} updated`,
          });
        }
      } else if (type === EntityFormType.Person) {
        const payload = buildPersonCreateRequest(values as PersonEntityFormValues);
        const response = await createPersonMutation.mutateAsync({ body: payload });
        navigate(ROUTE.ENTITIES);
        addSuccessNotification({
          content: `${response.person_details?.first_name} ${response.person_details?.last_name} added`,
        });
      } else {
        const businessValues = values as BusinessEntityFormValues;

        // Phase 1: create the business — or, on a retry after a partial failure, update the
        // already-created one so business-field edits still persist without re-creating it.
        let entityId = createdEntityId;
        let businessName: string | undefined;
        if (entityId) {
          const response = await updateBusinessMutation.mutateAsync({
            path: { entity_id: entityId },
            body: buildBusinessUpdateRequest(businessValues),
          });
          businessName = response.business_details?.business_name;
        } else {
          const response = await createBusinessMutation.mutateAsync({
            body: buildBusinessCreateRequest(businessValues),
          });
          entityId = response.id;
          businessName = response.business_details?.business_name;
          setCreatedEntityId(entityId);
        }

        // Phase 2: reconcile the selected associated persons onto the created business. If this
        // fails, createdEntityId is already set, so a retry updates the business and re-sends the
        // set (the PUT is idempotent) — nothing entered is lost.
        await saveAssociatedPersons(entityId, businessValues);

        navigate(ROUTE.ENTITIES);
        addSuccessNotification({ content: `${businessName} added` });
      }
    } catch (error: any) {
      addDangerNotification({ content: error?.message ?? 'Unable to save entity' });
    }
  }, handleSubmitErrors);

  const canWrite = isAuthorized({ permission: 'entities', permissionLevel: 'write' });
  const isSaving = form.formState.isSubmitting;

  const submitButton = (
    <Button onClick={handleSubmit} disabled={!canWrite || isSaving} size="sm" type="button">
      {!id && <Icon.Plus />}
      {id ? (isSaving ? 'Saving…' : 'Save Changes') : isSaving ? 'Creating…' : 'Create Entity'}
    </Button>
  );

  return (
    <FormProvider {...form}>
      <PageActions>{submitButton}</PageActions>
      <Loading isLoading={isLoading} fullHeight>
        {!canWrite && (
          <div className="px-(--page-margin) pt-6">
            <RestrictedBanner />
          </div>
        )}

        {RenderFields<Params>({
          sections: [
            {
              fields: [
                {
                  id: 'id',
                  label: 'ID',
                  hide: !id,
                  children: ({ value }) => (
                    <Input
                      variant="copy"
                      readOnly
                      showCopyButton
                      value={(value as string) ?? ''}
                      className="[&_input]:font-mono"
                    />
                  ),
                },
                {
                  id: 'type',
                  label: 'Entity Type',
                  children: () => (
                    <EntityTypeSelector
                      type={type}
                      isReadOnly={!!id}
                      onSelect={(next) => navigate(`${ROUTE.ENTITIES}/edit/${next}`)}
                    />
                  ),
                },
              ],
            },
          ],
        })}
        <Separator />

        <AnimatePresence mode="wait" initial={false} onExitComplete={handleFormExited}>
          {isFormShown && (
            <motion.div
              key={displayType}
              data-disabled={!canWrite}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {displayType === EntityFormType.Person ? (
                <PersonEntity id={id} disableForm={!canWrite} />
              ) : (
                <BusinessEntity id={id} disableForm={!canWrite} />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between px-(--page-margin) py-6">
          <Button onClick={() => navigate(ROUTE.ENTITIES)} variant="secondary" size="sm" type="button">
            {id ? 'Back' : 'Cancel'}
          </Button>
          {submitButton}
        </div>
      </Loading>
    </FormProvider>
  );
};
